import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Dimensions } from 'react-native';
import TextM from '@common/components/text/TextM';
import { theme } from '@common/theme/theme';
import Animated, {
  BounceInRight, 
  FadeOut,
  useSharedValue,
  withTiming,
  withSequence,
  Easing,
  useAnimatedStyle,
} from 'react-native-reanimated';
import * as amplitude from '@amplitude/analytics-react-native';
import { connect } from 'react-redux';
import { apiRequest } from '@data/redux/actions/api';
import TouchableRippleM from '@common/components/button/TouchableRippleM';
import { PlaySoundM } from '@common/components/playSound/PlaySoundM';
import { isMobile} from "@common/utils/mobileUtils";
import { ChampionImageM } from "../../../student/champions/ChampionImageM"

const TIMER_DURATION = 10000;

const CheckpointLaunchPopup = ({ 
  visible, 
  student,
  width = 380,
  onAccept,
  onReject,
  studentCardHeight = 180,
  arrowPosition = "left",
  description,
  acceptText = "Oui, vas-y",
  rejectText = "Non"
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const opacity = useSharedValue(visible ? 1 : 0);
  const scale = useSharedValue(1);
  const screenWidth = Dimensions.get('window').width;
  const isMobile = screenWidth <= 700;

  // Show/hide popup opacity & scale
  useEffect(() => {
    if (visible) {
      amplitude.track("checkpoint.launchpopup.display");

      opacity.value = withTiming(1, {
        duration: 150,
        easing: Easing.bezier(0.25, 0.1, 0.25, 1),
      });

      scale.value = withSequence(
        withTiming(1.05, { duration: 100 }),
        withTiming(1, { duration: 100 })
      );
    } else {
      opacity.value = withTiming(0, {
        duration: 200,
        easing: Easing.bezier(0.25, 0.1, 0.25, 1),
      });
    }
  }, [visible]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ scale: scale.value }],
    };
  });

  const handleAccept = (isAutoClick = false) => {
    PlaySoundM.play(require('../../../../assets/sounds/select_002.mp3'));
    amplitude.track(isAutoClick ? "checkpoint.launchpopup.auto_accept" : "checkpoint.launchpopup.accept");
    if (onAccept) {
      onAccept(isAutoClick);
    }
  };

  const handleReject = () => {
    PlaySoundM.play(require('../../../../assets/sounds/select_002.mp3'));
    amplitude.track("checkpoint.launchpopup.reject");
    if (onReject) {
      onReject();
    }
  };

  return (
    <View style={[
      styles.container,
      isMobile ? {height: studentCardHeight} :
      {
        width: '100%',
        height: isMobile ? 180 : undefined,
        overflow: 'visible',
      }
    ]}>
      <Animated.View
        entering={BounceInRight.duration(500).springify()}
        exiting={FadeOut.duration(100)}
      >
        <Animated.View
          style={[
            styles.popupContainer,
              isMobile?
            {
                width: '90%',
                maxWidth: 340,
                minWidth: 180,
                backgroundColor: '#fff',
                overflow: 'visible',
                height: 180 ,
            } : {width},
            animatedStyle
          ]}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
        <View style={styles.contentWrapper}>
          <View style={styles.content}>
            <ChampionImageM name={"sam"}
                            width={80}
            />
            <TextM
              color={theme.colors.grey}
              style={styles.description}
              wrap
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {description || `${student?.display_name} vient de lever la main, est-ce que je lui demande ce qu'il veut ?`}
            </TextM>

            <View style={styles.buttonContainer}>
              <TouchableRippleM
                style={[styles.button, styles.rejectButton]}
                onPress={handleReject}
                autoClickDuration={TIMER_DURATION}
                autoClickColor={theme.colors.error}
                autoClickPaused={!visible || isHovered}
                onAutoClick={handleReject}
              >
                <TextM color={theme.colors.white} fontWeight="Bold" fontSize={13}>
                  {rejectText || "Non"}
                </TextM>
              </TouchableRippleM>

              <TouchableRippleM
                style={[styles.button, styles.acceptButton]}
                onPress={() => handleAccept(false)}
              >
                <TextM color={theme.colors.white} fontWeight="Bold" fontSize={13}>
                  {acceptText || "Oui, vas y"}
                </TextM>
              </TouchableRippleM>
            </View>
          </View>
        </View>
        </Animated.View>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
  },
  popupContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: 16,
    shadowColor: theme.colors.black,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },

  contentWrapper: {
    flex: 1,
    overflow: 'hidden',
  },
  content: {
    padding: 16,
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: "center"
  },
  description: {
    marginBottom: 16,
    lineHeight: 20,
    fontSize: 14,
    textAlign: 'center',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 'auto',
  },
  button: {
    flex: 1,
    padding: 10,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    width: 100
  },
  rejectButton: {
    backgroundColor: theme.colors.errorLight,
  },
  acceptButton: {
    backgroundColor: theme.colors.primary,
  },

  progressContainer: {
    width: '100%',
    backgroundColor: theme.colors.white,
    padding: 0,
    margin: 0,
    overflow: 'hidden',
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
  },
  timerText: {
    textAlign: 'center',
    marginBottom: 2,
    marginTop: 2,
  },
  progressBar: {
    height: 3,
    borderRadius: 0,
  },
});

const mapStateToProps = (state) => ({
  currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
      (x) => x?.id === state.liveMode?.liveSessionId
  ),
});

const mapDispatchToProps = {
  apiRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(CheckpointLaunchPopup); 